import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { UNSAFE_DataRouterContext, useInRouterContext, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../../api/admin';
import { useUserStore } from '../../../stores/userStore';
import useAdminDraftGuard from '../useAdminDraftGuard';

const statusLabels = {
  PENDING: '접수',
  REVIEWING: '검토 중',
  ACTIONED: '조치 완료',
  DISMISSED: '반려',
};

const typeLabels = {
  USER: '회원',
  ALBUM_REVIEW: '앨범 리뷰',
  COMMENT: '댓글',
  PLAYLIST: '플레이리스트',
};

const reasonLabels = {
  SPAM: '스팸·홍보',
  HARASSMENT: '괴롭힘·위협',
  HATE_OR_ABUSE: '혐오·모욕',
  SEXUAL_OR_INAPPROPRIATE: '성적·부적절',
  MISINFORMATION: '거짓·오해 유발',
  COPYRIGHT: '저작권·권리 침해',
  OTHER: '기타',
};

const reportValue = (report) => ({ status: report.status, adminNote: report.adminNote || '' });
const sameValue = (a, b) => a.status === b.status && a.adminNote === b.adminNote;
const validFilter = (value) =>
  value === 'ALL'
    ? ''
    : Object.prototype.hasOwnProperty.call(statusLabels, value)
      ? value
      : 'PENDING';

function ReportCard({ report, value, changed, saving, locked, error, onEdit, onSave }) {
  return (
    <Paper
      component="section"
      aria-label={`신고 #${report.id}`}
      variant="outlined"
      sx={{ p: 2, minWidth: 0, containerType: 'inline-size' }}
    >
      <Stack
        spacing={1.5}
        sx={{
          overflowWrap: 'anywhere',
          '@container (max-width: 360px)': {
            '& .MuiAlert-root': { flexDirection: 'column', px: 1 },
            '& .MuiAlert-icon': { pb: 0 },
          },
        }}
      >
        <Stack
          direction="row"
          useFlexGap
          spacing={1}
          sx={{ flexWrap: 'wrap', alignItems: 'center' }}
        >
          <Chip size="small" label={statusLabels[report.status] || report.status} />
          <Typography fontWeight={700}>
            {typeLabels[report.contentType] || report.contentType} #{report.contentId}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {reasonLabels[report.reason] || report.reason}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {report.createdAt}
          </Typography>
        </Stack>
        <Typography variant="body2">
          신고자: {report.reporterNickname} (#{report.reporterId}) · 대상:{' '}
          {report.reportedUserNickname} (#{report.reportedUserId})
        </Typography>
        {report.details ? (
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>{report.details}</Typography>
        ) : null}
        {error && <Alert severity="error">{error}</Alert>}
        <Stack
          direction="row"
          useFlexGap
          spacing={1.5}
          sx={{ flexWrap: 'wrap', '@container (max-width: 500px)': { flexDirection: 'column' } }}
        >
          <TextField
            select
            SelectProps={{ native: true }}
            size="small"
            label="상태"
            value={value.status}
            disabled={saving}
            onChange={(e) => onEdit({ ...value, status: e.target.value })}
            sx={{ width: 150, maxWidth: '100%' }}
          >
            {Object.entries(statusLabels).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </TextField>
          <TextField
            sx={{ flex: 1, minWidth: 0 }}
            inputProps={{ maxLength: 1000 }}
            label="관리자 메모"
            multiline
            disabled={saving}
            onChange={(e) => onEdit({ ...value, adminNote: e.target.value })}
            size="small"
            value={value.adminNote}
          />
          <Button disabled={locked || !changed} onClick={onSave} variant="contained">
            {saving ? '저장 중…' : '저장'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function SafetySession({ filter, onFilterChange }) {
  const [reports, setReports] = useState([]);
  const [drafts, setDrafts] = useState({});
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveErrors, setSaveErrors] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const pending = useRef(false);
  const alive = useRef(true);
  const sequence = useRef(0);
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const guard = useAdminDraftGuard(drafts, { busy: savingId !== null });
  const load = useCallback(
    async (saved = false) => {
      const seq = ++sequence.current;
      setLoading(true);
      setError('');
      try {
        const response = await adminApi.getSafetyReports(filter || undefined);
        if (!alive.current || seq !== sequence.current) return;
        const incoming = response.data || [];
        setReports((previous) => [
          ...incoming,
          ...previous.filter(
            (row) => draftsRef.current[row.id] && !incoming.some((item) => item.id === row.id),
          ),
        ]);
      } catch (e) {
        if (alive.current && seq === sequence.current)
          setError(
            saved
              ? '저장은 완료됐지만 최신 신고 목록을 불러오지 못했습니다. 다시 조회해주세요.'
              : e.message || '신고 목록을 불러오지 못했습니다.',
          );
      } finally {
        if (alive.current && seq === sequence.current) setLoading(false);
      }
    },
    [filter],
  );
  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
      sequence.current += 1;
    };
  }, [load]);
  const edit = (report, value) => {
    setSavedId(null);
    setSaveErrors((previous) => ({ ...previous, [report.id]: '' }));
    setDrafts((previous) => {
      const next = { ...previous };
      if (sameValue(value, reportValue(report))) delete next[report.id];
      else next[report.id] = value;
      return next;
    });
  };
  const save = async (report) => {
    if (pending.current || loading || !drafts[report.id]) return;
    const snapshot = { ...drafts[report.id] };
    pending.current = true;
    setSavingId(report.id);
    setSavedId(null);
    setSaveErrors((previous) => ({ ...previous, [report.id]: '' }));
    try {
      await adminApi.updateSafetyReport(report.id, snapshot);
      if (!alive.current) return;
      setReports((previous) =>
        previous.map((row) => (row.id === report.id ? { ...row, ...snapshot } : row)),
      );
      const next = { ...draftsRef.current };
      delete next[report.id];
      draftsRef.current = next;
      setDrafts(next);
      setSavedId(report.id);
      await load(true);
    } catch (e) {
      if (alive.current)
        setSaveErrors((previous) => ({
          ...previous,
          [report.id]: e.message || '신고 상태를 저장하지 못했습니다.',
        }));
    } finally {
      if (alive.current) {
        pending.current = false;
        setSavingId(null);
      }
    }
  };
  return (
    <Stack
      spacing={2}
      sx={{
        minWidth: 0,
        overflowWrap: 'anywhere',
        containerType: 'inline-size',
        '@container (max-width: 360px)': {
          '& > .MuiAlert-root': { flexDirection: 'column', px: 1 },
          '& > .MuiAlert-root .MuiAlert-action': { ml: 0, pl: 0 },
        },
      }}
    >
      <Typography variant="h6">신고 관리</Typography>
      <Alert severity="info">
        대상 콘텐츠와 회원 이력을 확인한 뒤 기존 회원·리뷰 관리에서 필요한 조치를 먼저 실행하고, 그
        결과를 상태와 메모에 남겨주세요.
      </Alert>
      <TextField
        select
        SelectProps={{ native: true }}
        label="상태 필터"
        InputLabelProps={{ shrink: true }}
        size="small"
        value={filter}
        disabled={savingId !== null}
        sx={{ width: 220, maxWidth: '100%' }}
        onChange={(e) => {
          if (pending.current) return;
          if (!dataRouter && !guard.confirmDiscard()) return;
          onFilterChange(e.target.value);
        }}
      >
        <option value="">전체</option>
        {Object.entries(statusLabels).map(([id, label]) => (
          <option key={id} value={id}>
            {label}
          </option>
        ))}
      </TextField>
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" disabled={loading || savingId !== null} onClick={() => load()}>
              재시도
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {savedId !== null && (
        <Typography role="status">신고 #{savedId}의 상태와 메모를 저장했습니다.</Typography>
      )}
      {loading && (
        <Box
          role="status"
          aria-label="신고 목록 조회 중"
          sx={{ display: 'flex', justifyContent: 'center', py: 3 }}
        >
          <CircularProgress />
        </Box>
      )}
      {!loading && !error && reports.length === 0 && (
        <Alert severity="success">해당 상태의 신고가 없습니다.</Alert>
      )}
      {reports.map((report) => (
        <ReportCard
          key={report.id}
          report={report}
          value={drafts[report.id] || reportValue(report)}
          changed={Boolean(drafts[report.id])}
          saving={savingId === report.id}
          locked={savingId !== null || loading}
          error={saveErrors[report.id]}
          onEdit={(value) => edit(report, value)}
          onSave={() => save(report)}
        />
      ))}
    </Stack>
  );
}

function RoutedSafety({ account }) {
  const [params, setParams] = useSearchParams();
  const filter = validFilter(params.get('safetyStatus'));
  return (
    <SafetySession
      key={JSON.stringify([account, filter])}
      filter={filter}
      onFilterChange={(value) => {
        const next = new URLSearchParams(params);
        next.set('safetyStatus', value || 'ALL');
        setParams(next, { replace: true });
      }}
    />
  );
}
function LocalSafety({ account }) {
  const [filter, setFilter] = useState('PENDING');
  return (
    <SafetySession
      key={JSON.stringify([account, filter])}
      filter={filter}
      onFilterChange={setFilter}
    />
  );
}
export default function ContentSafetyManagement() {
  const user = useUserStore((state) => state.user);
  const routed = useInRouterContext();
  const account = JSON.stringify([user?.id, user?.role]);
  return routed ? (
    <RoutedSafety account={account} />
  ) : (
    <LocalSafety key={account} account={account} />
  );
}
